import React, { useState, useEffect } from 'react';
import { Unlock, Loader2, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';

const MaintenanceExit = () => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Vérifier si la maintenance est active
  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const response = await api.get('/maintenance/status');
        if (!response.data.maintenance_mode) {
          // Maintenance désactivée, rediriger vers l'accueil
          navigate('/', { replace: true });
        }
      } catch (err) {
        // En cas d'erreur, rediriger vers l'accueil
        navigate('/', { replace: true });
      } finally {
        setCheckingStatus(false);
      }
    };
    
    checkMaintenance();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await api.post('/maintenance/exit', { password });
      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/');
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Mot de passe incorrect');
    } finally {
      setLoading(false);
    }
  };

  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#FF6B35]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#FF6B35] mb-2">IPTV4KFR</h1>
          <p className="text-[#6B6B6E] text-sm">Sortie de maintenance</p>
        </div>

        {success ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#00FF88]/10 border border-[#00FF88] rounded-xl p-8 text-center"
          >
            <CheckCircle className="h-16 w-16 text-[#00FF88] mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Maintenance désactivée !</h2>
            <p className="text-[#B8B8BA] text-sm">Le site est de nouveau accessible.</p>
            <p className="text-[#6B6B6E] text-xs mt-4">Redirection en cours...</p>
          </motion.div>
        ) : (
          <div className="bg-[#1A1A1D] rounded-xl p-6 sm:p-8 border border-[#3A3A3D]">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-[#FFB800]/10 rounded-full flex items-center justify-center">
                <Unlock className="h-8 w-8 text-[#FFB800]" />
              </div>
            </div>

            <h2 className="text-xl font-bold text-white text-center mb-2">
              Désactiver la maintenance
            </h2>
            <p className="text-[#B8B8BA] text-sm text-center mb-6">
              Entrez le mot de passe administrateur pour remettre le site en ligne.
            </p>

            {error && (
              <div className="bg-[#FF3B5C]/10 border border-[#FF3B5C] rounded-lg p-3 mb-4 flex items-center space-x-2">
                <AlertCircle className="h-5 w-5 text-[#FF3B5C] shrink-0" />
                <span className="text-[#FF3B5C] text-sm">{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-[#E8E8E8] text-sm mb-2">
                  Mot de passe de sortie
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#FF6B35] focus:outline-none text-base"
                  placeholder="••••••••"
                  required
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={loading || !password}
                className="w-full btn-primary py-3 flex items-center justify-center space-x-2 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Vérification...</span>
                  </>
                ) : (
                  <>
                    <Unlock className="h-5 w-5" />
                    <span>Désactiver la maintenance</span>
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 pt-4 border-t border-[#3A3A3D]">
              <Link
                to="/maintenance"
                className="flex items-center justify-center space-x-2 text-[#B8B8BA] hover:text-white transition-colors text-sm"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Retour à la page de maintenance</span>
              </Link>
            </div>
          </div>
        )}

        {/* Admin login link */}
        <p className="mt-6 text-center text-[#6B6B6E] text-sm">
          Vous êtes admin ?{' '}
          <Link to="/admin/login" className="text-[#FF6B35] hover:underline">
            Connexion admin
          </Link>
        </p>
      </motion.div>
    </div>
  );
};

export default MaintenanceExit;
